import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../services/firebase';
import { CheckCircleIcon, XCircleIcon, ClockIcon } from '@heroicons/react/24/outline';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

interface InvitationDetails {
  id: string;
  email: string;
  businessName: string;
  agencyName: string;
  role: string;
  status: string;
}

type PageState = 'loading' | 'form' | 'expired' | 'accepted' | 'error' | 'success';

export default function AcceptInvitationPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [pageState, setPageState] = useState<PageState>('loading');
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Fetch invitation details on mount
  useEffect(() => {
    async function fetchInvitation() {
      if (!token) {
        setPageState('error');
        setError('Invalid invitation link');
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/invitations/token/${token}`);
        const data = await response.json();

        if (!response.ok) {
          if (response.status === 410) {
            // Invitation expired or already accepted
            if (data.invitation?.status === 'expired') {
              setPageState('expired');
            } else if (data.invitation?.status === 'accepted') {
              setPageState('accepted');
            }
            setInvitation(data.invitation);
          } else {
            setPageState('error');
            setError(data.error || 'Invitation not found');
          }
          return;
        }

        setInvitation(data.invitation);
        setPageState('form');
      } catch (err) {
        console.error('Error fetching invitation:', err);
        setPageState('error');
        setError('Failed to load invitation');
      }
    }

    fetchInvitation();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/invitations/token/${token}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.error?.includes('already exists')) {
          setError('An account with this email already exists. Please login instead.');
        } else {
          setError(data.error || 'Failed to accept invitation');
        }
        setSubmitting(false);
        return;
      }

      // Sign in with custom token
      if (data.customToken) {
        await signInWithCustomToken(auth, data.customToken);
        setPageState('success');

        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        // Fallback: redirect to login
        setPageState('success');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (err: any) {
      console.error('Error accepting invitation:', err);
      setError(err.message || 'Failed to accept invitation');
      setSubmitting(false);
    }
  };

  // Loading state
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading invitation...</p>
        </div>
      </div>
    );
  }

  // Expired state
  if (pageState === 'expired') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <ClockIcon className="h-16 w-16 text-yellow-500 mx-auto" />
            <h2 className="mt-4 text-2xl font-bold text-gray-900">Invitation Expired</h2>
            <p className="mt-2 text-gray-600">
              This invitation to join <strong>{invitation?.businessName}</strong> has expired.
            </p>
            <p className="mt-4 text-sm text-gray-500">
              Please contact your agency to request a new invitation.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Already accepted state
  if (pageState === 'accepted') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto" />
            <h2 className="mt-4 text-2xl font-bold text-gray-900">Already Accepted</h2>
            <p className="mt-2 text-gray-600">
              This invitation has already been accepted.
            </p>
            <Link
              to="/login"
              className="mt-6 inline-block btn-primary"
            >
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (pageState === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <XCircleIcon className="h-16 w-16 text-red-500 mx-auto" />
            <h2 className="mt-4 text-2xl font-bold text-gray-900">Invalid Invitation</h2>
            <p className="mt-2 text-gray-600">{error}</p>
            <Link
              to="/login"
              className="mt-6 inline-block btn-primary"
            >
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (pageState === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto" />
            <h2 className="mt-4 text-2xl font-bold text-gray-900">Welcome!</h2>
            <p className="mt-2 text-gray-600">
              Your account has been created successfully.
            </p>
            <p className="mt-4 text-sm text-gray-500">
              Redirecting to your dashboard...
            </p>
            <div className="mt-4 animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  // Form state
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900">You're Invited!</h2>
            <p className="mt-2 text-gray-600">
              <strong>{invitation?.agencyName}</strong> has invited you to manage
            </p>
            <p className="text-lg font-semibold text-primary-600 mt-1">
              {invitation?.businessName}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="label">
                Email
              </label>
              <input
                type="email"
                id="email"
                value={invitation?.email || ''}
                disabled
                className="input bg-gray-50 text-gray-500 cursor-not-allowed"
              />
            </div>

            <div>
              <label htmlFor="name" className="label">
                Full Name
              </label>
              <input
                type="text"
                id="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input"
                placeholder="Enter your full name"
              />
            </div>

            <div>
              <label htmlFor="password" className="label">
                Password
              </label>
              <input
                type="password"
                id="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                placeholder="Create a password (min 8 characters)"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="label">
                Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input"
                placeholder="Confirm your password"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full"
            >
              {submitting ? 'Creating Account...' : 'Accept Invitation'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-500">
            Already have an account?{' '}
            <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
              Sign in instead
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
